import React from 'react';
import { ArcButton, ArcDialog, ArcMenuItem, ArcOutlinedInput, ArcOutlinedSelect, ArcSkeleton, ArcStack, ArcSwitch, ArcText } from '@src/core/ui';
import { SettingInputFieldProps, SettingPopupProps, SettingSelectFieldProps, SettingsGroupProps, SettingSwitcherFieldProps } from './types';
import { useSetSettingsMutation } from '@src/features/settings/redux';
import { UnitValueEnum } from '@src/features/settings/entities';
import { toast } from 'react-toastify';
import { EMPTY_VALUE } from '@src/core/config';
import { InputAdornment } from '@mui/material';
import { SettingItemEntity, SwitcherType, useGetSettingsQuery, useNumericRanges } from '@src/core/features/settings';
import { NUMERIC_SETTINGS_KEYS } from '@src/features/settings/pages/settings-page/types';
import { droneSettingsKeysToExclude, SettingKey } from '@src/features/settings/config/settings-constants';
import { arcToFixed } from '@src/core/extensions';
import { useNavigate } from 'react-router-dom';
import { routesConfig } from '@src/core/routes/routes-config';


const Settings: React.FC = () => {
  const { data = { group: [] }, isLoading: isSettingsLoading, refetch} = useGetSettingsQuery();
  const [ setSettingItem ] = useSetSettingsMutation();
  const navigate = useNavigate();

  const onSetSettingItem = (settings: SettingItemEntity[]) => {
    setSettingItem(settings).unwrap().then(() => {
      toast.success('SET SETTING SUCCESS TEXT');
    }).catch(() => {});  
  };

  if (isSettingsLoading) {
    return (
      <ArcStack spacing={2}>
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
        <ArcSkeleton height={50} />
      </ArcStack>
    );
  }

  return (
    <ArcStack spacing={2}>
      <ArcStack spacing={5}>
        {data.group.map((group) => {
          return (
            <SettingGroup 
              key={group.title}
              title={group.title} 
              settings={group.items} 
              isSubmitting={isSettingsLoading} 
              onSetSettingItem={onSetSettingItem} 
              refetch={refetch} />
          );
        })}
      </ArcStack>
      <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
        <ArcText.h5>Controller</ArcText.h5>
        <ArcText.h5
          sx={{ color: 'brand.main', cursor: 'pointer' }}
          onClick={() => navigate(routesConfig.settingsController)}
        >
          Configure
        </ArcText.h5>
      </ArcStack>
    </ArcStack>
  );
};


const SettingGroup: React.FC<SettingsGroupProps> = (props) => {
  const { title, settings, isSubmitting, onSetSettingItem, refetch } = props;
  return (
    <ArcStack spacing={2}>
      <ArcText.h3> {title} </ArcText.h3>
      <ArcStack spacing={1}>
        {settings.map((setting) => {
          if(droneSettingsKeysToExclude.includes(setting.key)) {
            return null;
          }
          if (setting.unitType === UnitValueEnum.DROPDOWN) {
            return (
              <SettingSelectField 
                key={`${setting.key}-${setting.value}`}
                setting={setting} 
                isSubmitting={isSubmitting} 
                onSetSettingItem={onSetSettingItem} 
                refetch={refetch} />
            );
          }

          if (setting.unitType === UnitValueEnum.BOOLEAN) {
            return (
              <SettingSwitcherField 
                key={`${setting.key}-${setting.value}`}  
                setting={setting}
                isSubmitting={isSubmitting}  
                onSetSettingItem={onSetSettingItem} />
            );
          }

          return (
            <SettingItem 
              key={`${setting.key}-${setting.value}`}
              setting={setting} 
              isSubmitting={isSubmitting}
              onSetSettingItem={onSetSettingItem} />
          );
        })}
      </ArcStack>
    </ArcStack>
  );
};


const SettingSwitcherField: React.FC<SettingSwitcherFieldProps> = (props) => {
  const { setting, isSubmitting, onSetSettingItem } = props;

  return (
    <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
      <ArcText.h5> {setting.name} </ArcText.h5>
      <ArcSwitch.brand 
        disabled={isSubmitting} 
        checked={setting.value == SwitcherType.ON}
        onChange={(_, checked) => {
          const payload = { ...setting, value: checked ? SwitcherType.ON : SwitcherType.OFF };
          onSetSettingItem([payload]);
        }}  />
    </ArcStack>
  );
};

const SettingSelectField: React.FC<SettingSelectFieldProps> = (props) => {
  const { setting, isSubmitting, onSetSettingItem, refetch } = props;

  return (
    <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
      <ArcText.h5> {setting.name} </ArcText.h5>
      <ArcOutlinedSelect 
        sx={{ width: 250 }}
        value={setting.value}
        disabled={isSubmitting}
        onChange={async (e) => {
          const current = e.target.value;
          if (typeof current === 'string') {
            const { values, ...rest } = setting;
            const payload = { ...rest, value: current };
            onSetSettingItem([payload]);
            if(setting.key === SettingKey.units) {
              refetch();
            } 
          }
        }}>
        {(setting.values ?? []).map(({ value, name }) => (
          <ArcMenuItem 
            key={value} 
            value={value}> 
            {name} 
          </ArcMenuItem>
        ))}
      </ArcOutlinedSelect>
    </ArcStack>
  );
};

const SettingItem: React.FC<SettingInputFieldProps> = (props) => {

  const { setting, isSubmitting, onSetSettingItem } = props;
  const [value, setValue] = React.useState(setting.value ?? EMPTY_VALUE);
  const [showPopup, setShowPopup] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setShowPopup(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 0);
  };

  const handleSaveFromPopup = (newValue: string) => {
    setValue(newValue);
    onSetSettingItem([{ ...setting, value: newValue }]);
    setShowPopup(false);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    }, 0);
  };

  return (
    <>
      <ArcStack spacing={1} direction='row' alignItems='center' justifyContent='space-between'>
        <ArcText.h5 onClick={() => setShowPopup(true)}> {setting.name} </ArcText.h5>
        <ArcOutlinedInput 
          ref={inputRef}
          value={arcToFixed(Number(value), 1)} 
          sx={{ width: 250 }} 
          aria-readonly={true}
          onClick={() => setShowPopup(true)}
          onFocus={(e) => e.target.style.cursor = 'pointer'}
          disabled={isSubmitting}
          slotProps={{ input: { readOnly: true, endAdornment: <InputAdornment position='end'> <ArcText.h5> {setting.unitValue} </ArcText.h5> </InputAdornment> } }}
        />
      </ArcStack>
      <SettingPopup 
        open={showPopup}
        onClose={handleClose}
        onSave={handleSaveFromPopup} 
        setting={setting} 
        isSubmitting={isSubmitting} 
        currentValue={value}
      />
    </>
  );
};

const isNumericSetting = (setting: SettingItemEntity): boolean => {
  return NUMERIC_SETTINGS_KEYS.includes(setting.key);
};

const SettingPopup: React.FC<SettingPopupProps> = (props) => {
  const { setting, isSubmitting, onSave, open, onClose, currentValue } = props;

  const isNumeric = isNumericSetting(setting);
  const numericRanges = useNumericRanges();

  const [displayValue, setDisplayValue] = React.useState<string>("");

  React.useEffect(() => {
    const initial = currentValue ?? setting.value ?? EMPTY_VALUE;

    if (isNumeric && initial !== "" && !isNaN(Number(initial))) {
      setDisplayValue(arcToFixed(Number(initial), 1));
    } else {
      setDisplayValue(String(initial));
    }
  }, [currentValue, setting.value, open]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;

    if (isNumeric) {
      if (/^\d*\.?\d{0,1}$/.test(v) || v === "") {
        setDisplayValue(v);
      }
    } else {
      setDisplayValue(v);
    }
  };

  const handleSave = () => {
    if (isNumeric) {
      onSave(arcToFixed(Number(displayValue || 0), 1));
    } else {
      onSave(displayValue);
    }
  };

  return (
    <ArcDialog
      open={open ?? false}
      onClose={onClose}
      title={`Change ${setting.name}`}
      contextSX={{ px: 4, paddingBottom: 3 }}
    >
      <ArcStack spacing={2}>
        <ArcOutlinedInput
          autoComplete="off"
          label={setting.name}
          value={displayValue}
          onChange={handleChange}
          minNumber={numericRanges[setting.key]?.min}
          maxNumber={numericRanges[setting.key]?.max}
          inputProps={
            isNumeric
              ? {
                pattern: "^\\d*(\\.\\d?)?$",
                inputMode: "decimal",
              }
              : {}
          }
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <ArcText.h5>{setting.unitValue}</ArcText.h5>
                </InputAdornment>
              ),
            },
          }}
        />

        <ArcButton.brand onClick={handleSave} isLoading={isSubmitting}>
          Save
        </ArcButton.brand>
      </ArcStack>
    </ArcDialog>
  );
};


export default Settings;